import type { WidgetEffectParams } from '@swf/core/store';
import { pageChange } from '@swf/core/utils/actions';
import type { BasicSportsWidgetConfig } from '@swf/sports/pages/types';
import { sportsWidgetPollingBehavior } from '@swf/sports/utils/actions';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import getErrorStatusCode from '@swf/sports/utils/getErrorStatusCode';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';
import { hasRequestAborted } from '@swf/sports/utils/hasRequestAborted';

import type { RacePageSuccessResponse, WidgetDispatch, WidgetSelect } from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { dispatch, widget, rootState, select, actions } = params;
    const { racePageRequested, quickSwitcherRequested, clear }: WidgetDispatch =
      dispatch[widget.uid];
    const widgetSelect: WidgetSelect = select[widget.uid];

    sportsWidgetPollingBehavior(params).subscribe({
      next: (pollingSource) =>
        racePageRequested({ isBackgroundLoading: pollingSource === 'INTERVAL' }),
      complete: clear
    });

    pageChange(actions)?.subscribe(() => quickSwitcherRequested());

    if (!widgetSelect.hasPreloaded(rootState)) {
      await racePageRequested();
    }

    quickSwitcherRequested();
  },
  async racePageRequested(
    _payload: undefined,
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ) {
    const { dispatch, request, widget, select, rootState } = params;
    const { dataLoadSuccess, dataLoadFailure }: WidgetDispatch = dispatch[widget.uid];
    const { meetingId, raceId: raceTime } = select.sports.getUrlParams(rootState);
    const raceId = `${meetingId}.${raceTime}`;
    const sportsRequest = buildSportsRequest(request, rootState);

    try {
      const {
        body: { data }
      } = await sportsRequest<{ data: RacePageSuccessResponse }>({
        path: `/race-page/${meetingId}/${raceId}`,
        signal: getWidgetCancellationSignal(params)
      });
      dataLoadSuccess({ ...data, raceId });
    } catch (error) {
      if (!hasRequestAborted(error)) {
        dataLoadFailure(getErrorStatusCode(error));
      }
    }
  },
  async quickSwitcherRequested(
    _payload: undefined,
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ) {
    const { dispatch, rootState, select } = params;

    const { meetingId, raceId: raceTime } = select.sports.getUrlParams(rootState);
    const raceId = `${meetingId}.${raceTime}`;

    dispatch.quickSwitcher.quickSwitcherRequested({ hierarchyLevel: 'race', facetId: raceId });
  }
};
